import jsonExport from "jsonexport/dist";
import {
  downloadCSV,
  FilterLiveForm,
  useGetIdentity,
  useGetList,
  useListContext,
  useTranslate,
  type Exporter,
} from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { BulkActionsToolbar } from "@/components/admin/bulk-actions-toolbar";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { SortButton } from "@/components/admin/sort-button";
import { PageSpinner } from "@/components/ui/page-spinner";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { todasAsLinhas } from "@/components/atomic-crm/providers/supabase/todasAsLinhas";
import { CabecalhoPagina } from "../bento/ui";
import { FaixaIndicadores } from "../vendas/components/pecasPremium";
import { formatBRL } from "@/lib/format";

import type { Company, Contact, Sale, Tag } from "../types";
import { ContactListContent } from "../contacts/ContactListContent";
import { getContactDetail } from "../contacts/contact-details";
import { TopToolbar } from "../layout/TopToolbar";
import { SalesPersonFilter } from "../commons/SalesPersonFilter";
import { useProductModule } from "@/lib/useProductModule";
import { useSomenteLeitura } from "../commons/somenteLeitura";
import { BotaoOlhinho, ValoresOcultosProvider } from "../valores-ocultos/ValoresOcultos";
import { useMascara } from "../valores-ocultos/contexto";

type LanceFilter = 'all' | 'ok' | 'pendente' | 'falha' | 'nao_disponivel';

const lanceFilterOptions: { value: LanceFilter; label: string; classes: string }[] = [
  { value: 'all',            label: 'Todos',          classes: 'bg-muted text-foreground/80 hover:bg-[#E3E8F0]' },
  { value: 'ok',             label: 'Realizado',      classes: 'bg-ok-fundo text-ok hover:brightness-95' },
  { value: 'pendente',       label: 'Pendente',       classes: 'bg-muted text-foreground/80 hover:bg-[#E3E8F0]' },
  { value: 'falha',          label: 'Falha',          classes: 'bg-erro-fundo text-erro hover:brightness-95' },
  { value: 'nao_disponivel', label: 'Não disponível', classes: 'bg-alerta-fundo text-alerta hover:brightness-95' },
];

/**
 * Clientes entra no olhinho (09/10/2026): os três cartões, o selo de lance da
 * lista e o total da paginação escondem o número. A lista é a mesma de
 * Contatos (ContactListContent), que fica sem provedor e mostra tudo.
 */
export const ClientList = () => (
  <ValoresOcultosProvider>
    <ClientListInterno />
  </ValoresOcultosProvider>
);

const ClientListInterno = () => {
  const { identity } = useGetIdentity();
  const [salesPersonFilter, setSalesPersonFilter] = useState<string>('all');
  const [lanceFilter, setLanceFilter] = useState<LanceFilter>('all');
  const ehConsorcio = useProductModule() === "consortium";

  // Fetch contact_ids whose lance summary matches the selected category.
  // The view contacts_lance_summary só inclui contatos que TÊM cota com
  // próxima assembleia (não cobre "sem cota agendada" — esses ficam fora).
  const { data: summaries } = useGetList(
    "contacts_lance_summary",
    {
      filter:
        lanceFilter === 'ok'              ? { 'lance_ok@gt': 0 } :
        lanceFilter === 'falha'           ? { 'lance_failed@gt': 0 } :
        lanceFilter === 'pendente'        ? { 'lance_missing@gt': 0 } :
        lanceFilter === 'nao_disponivel'  ? { 'lance_cancelled@gt': 0 } :
        {},
      pagination: { page: 1, perPage: 1000 },
      // A view não tem `id`: sem sort o provider ordena por ele e leva 400.
      sort: { field: "contact_id", order: "ASC" },
    },
    // Lance é de consórcio: no Seguros nem consulta.
    { enabled: ehConsorcio && lanceFilter !== 'all' },
  );
  const lanceIdsFilter = useMemo(() => {
    if (lanceFilter === 'all') return {};
    const ids = (summaries ?? []).map((s: any) => s.contact_id);
    return { id: ids.length > 0 ? ids : [-1] }; // -1 forces empty result when no match
  }, [lanceFilter, summaries]);

  if (!identity) return <PageSpinner />;

  const permanentFilter = {
    has_sales: true,
    ...(salesPersonFilter !== 'all' ? { user_id: salesPersonFilter } : {}),
    ...lanceIdsFilter,
  };

  return (
    <List
      title={false}
      actions={false}
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
      exporter={exporter}
      filter={permanentFilter}
    >
      <ClientListLayout
        salesPersonFilter={salesPersonFilter}
        onSalesPersonFilterChange={setSalesPersonFilter}
        lanceFilter={lanceFilter}
        onLanceFilterChange={setLanceFilter}
      />
    </List>
  );
};

const ClientListLayout = ({
  salesPersonFilter,
  onSalesPersonFilterChange,
  lanceFilter,
  onLanceFilterChange,
}: {
  salesPersonFilter: string;
  onSalesPersonFilterChange: (v: string) => void;
  lanceFilter: LanceFilter;
  onLanceFilterChange: (v: LanceFilter) => void;
}) => {
  const { somenteLeitura } = useSomenteLeitura();
  const translate = useTranslate();
  const m = useMascara();
  const { data, isPending, filterValues } = useListContext<Contact>();
  const { identity } = useGetIdentity();
  // Carteira ativa (R$), cotas e CLIENTES distintos — clientes ativos é o
  // denominador do ticket médio por cliente, pedido do usuário (2026-08-25).
  const [overview, setOverview] = useState<{ carteira: number; cotas: number; clientes: number } | null>(null);
  // Persistência, cotas ativas e lance saem de servopa_vendas — dado de
  // consórcio. No Seguros nada disso existe, então nem renderiza nem consulta.
  const ehConsorcio = useProductModule() === "consortium";

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  useEffect(() => {
    if (!identity || isPending || !ehConsorcio) {
      return;
    }

    let mounted = true;

    const loadOverview = async () => {
      // RLS recorta o escopo (vendedor vê o dele, gerente o time) — mesma
      // régua do dashboard. Cliente distinto = contact_id (nome como reserva
      // pra venda ainda sem contato vinculado; a view não expõe CPF).
      // Em páginas: a carteira passa das 1000 linhas do PostgREST.
      const { data: rows, error } = await todasAsLinhas<{
        contact_id: number | null;
        nome_consorciado: string | null;
        valor_credito: number | null;
      }>(() =>
        supabase
          .from("servopa_vendas_with_details")
          .select("contact_id, nome_consorciado, valor_credito")
          .eq("status", "confirmed"),
      );

      if (!mounted) return;

      if (error) {
        console.warn("Failed to load clients overview:", error.message);
        setOverview({ carteira: 0, cotas: 0, clientes: 0 });
        return;
      }
      const lista = rows ?? [];
      const clientes = new Set(
        lista.map((r) =>
          r.contact_id != null
            ? `id:${r.contact_id}`
            : String(r.nome_consorciado ?? "").trim().toUpperCase(),
        ).filter(Boolean),
      );
      setOverview({
        carteira: lista.reduce((sum, r) => sum + Number(r.valor_credito || 0), 0),
        cotas: lista.length,
        clientes: clientes.size,
      });
    };

    loadOverview();

    return () => {
      mounted = false;
    };
  }, [identity, isPending, ehConsorcio]);

  if (!identity || isPending) return <PageSpinner />;

  if (!data?.length && !hasFilters && lanceFilter === 'all') {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <h2 className="text-2xl font-semibold mb-2">
          Nenhum cliente encontrado
        </h2>
        <p className="text-muted-foreground">
          Clientes são exibidos após a conclusão de vendas.
        </p>
      </div>
    );
  }

  const ticketMedio =
    overview && overview.clientes > 0 ? overview.carteira / overview.clientes : 0;

  return (
    <div className="flex flex-col gap-4">
      <CabecalhoPagina
        titulo="Clientes"
        descricao="Contatos com pelo menos uma venda."
        aoLadoDoTitulo={<BotaoOlhinho />}
        acoes={<ClientListActions />}
      />
      {ehConsorcio && (
        <FaixaIndicadores
          itens={[
            {
              rotulo: "Ticket médio",
              valor: overview ? m(formatBRL(ticketMedio)) : "…",
              detalhe: "carteira ativa ÷ clientes ativos",
            },
            {
              rotulo: "Cotas ativas",
              valor: overview ? m(overview.cotas.toLocaleString("pt-BR")) : "…",
              detalhe: overview ? `${m(formatBRL(overview.carteira))} em crédito` : undefined,
            },
            {
              rotulo: "Clientes ativos",
              valor: overview ? m(overview.clientes.toLocaleString("pt-BR")) : "…",
              detalhe: "com pelo menos uma cota ativa",
            },
          ]}
        />
      )}
      {/* Busca, vendedor, último lance e a lista num cartão só (Premium North). */}
      <div className="cartao bg-card">
        <div className="space-y-3 border-b border-border p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <FilterLiveForm>
              <SearchInput
                source="q"
                placeholder={translate("atomic.contacts.searchPlaceholder")}
                className="w-full"
              />
            </FilterLiveForm>
            {/* Filtro por vendedor: super-admin (todos) ou gerente (só a equipe dele,
                escopo aplicado dentro do SalesPersonFilter). Vendedor comum não vê. */}
            {(identity?.is_super_admin || identity?.is_manager) && (
              <SalesPersonFilter
                value={salesPersonFilter}
                onValueChange={onSalesPersonFilterChange}
                className="sm:w-64"
              />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Último lance registrado:</span>
            {lanceFilterOptions.map((opt) => {
              const active = lanceFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onLanceFilterChange(opt.value)}
                  className={`inline-flex h-7 cursor-pointer items-center rounded-full border px-3 text-xs font-semibold transition-colors ${opt.classes} ${active ? 'border-marinho ring-2 ring-marinho/25' : 'border-transparent'}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <ContactListContent showPersistencia={ehConsorcio} />
      </div>
      {!somenteLeitura && <BulkActionsToolbar />}
    </div>
  );
};

const ROTULOS_ORDEM = {
  last_seen: {
    DESC: "Ordenar por atividade mais recente",
    ASC: "Ordenar por atividade mais antiga",
  },
};

const ClientListActions = () => {
  const { somenteLeitura } = useSomenteLeitura();
  return (
    <TopToolbar>
      <SortButton fields={["last_seen"]} rotulos={ROTULOS_ORDEM} />
      {!somenteLeitura && <ExportButton exporter={exporter} />}
    </TopToolbar>
  );
};

const exporter: Exporter<Contact> = async (records, fetchRelatedRecords) => {
  const companies = await fetchRelatedRecords<Company>(
    records,
    "company_id",
    "companies",
  );
  const sales = await fetchRelatedRecords<Sale>(records, "user_id", "users");
  const tags = await fetchRelatedRecords<Tag>(records, "tags", "tags");

  const contacts = records.map((contact) => {
    const sale = contact.user_id != null ? sales[contact.user_id] : undefined;
    return {
      ...contact,
      company:
        contact.company_id != null
          ? companies[contact.company_id].name
          : undefined,
      sales: sale ? sale.name : "",
      tags: contact.tags.map((tagId) => tags[tagId].name).join(", "),
      email: getContactDetail(contact, "email"),
      phone: getContactDetail(contact, "phone"),
    };
  });
  return jsonExport(contacts, {}, (_err: any, csv: string) => {
    downloadCSV(csv, "clientes");
  });
};
